/**
 * @file boardroomRecorderPreviewNotes.ts
 * @brief DEV ONLY: sample notes for the recorder's UI preview, so the agenda,
 *        its picker and "Your notes" show something on /__preview.
 *
 * @details
 * DES 11-10-2026: "preview main koi agenda add nahin karsakta, usay bhi show
 * karwao, also notes bhi". The preview's stand-in user is not signed in, so
 * every notes request fails; this puts sample notes straight into the query
 * cache (the AgendaPreview / InterpretPreview pattern) and keeps them from
 * being refetched. Nothing is sent anywhere. Editing in the preview only
 * changes the screen (a save shows "Not saved").
 *
 * @author DES
 * @date 11-10-2026
 */

import type { QueryClient } from "@tanstack/react-query";
import { notesKeys } from "@/features/notes/api";
import type { VoiceNote } from "@/features/notes/schemas";

const at = "2026-10-10T09:00:00.000Z";

export const PREVIEW_AGENDA_NOTE: VoiceNote = {
  id: "preview-note-agenda",
  title: "Q4 planning agenda",
  source: "typed",
  status: "ready",
  points: [
    "Confirm the Q4 hiring plan",
    "Budget sign-off for the marketing launch",
    "Decide the January release date",
    "Agree owners for the customer pilots",
  ].join("\n"),
  created_at: at,
  updated_at: at,
};

export const PREVIEW_WRITING_NOTE: VoiceNote = {
  id: "preview-note-writing",
  title: "Web Meeting No.1",
  source: "typed",
  status: "ready",
  points: [
    "Imran wants the hiring plan by Friday",
    "Marketing budget looks 10% over, check with finance",
    "Ask Sara about the pilot customers",
  ].join("\n"),
  created_at: at,
  updated_at: at,
};

const PICKER_NOTES: VoiceNote[] = [
  PREVIEW_AGENDA_NOTE,
  {
    id: "preview-note-2",
    title: "Customer pilot checklist",
    source: "typed",
    status: "ready",
    points: ["Pick three pilot customers", "Weekly check-in call", "Success measures"].join("\n"),
    created_at: at,
    updated_at: at,
  },
  {
    id: "preview-note-3",
    title: "Board meeting prep",
    source: "typed",
    status: "ready",
    points: ["Revenue update", "Hiring", "Risks"].join("\n"),
    created_at: at,
    updated_at: at,
  },
];

/** Seed the cache once; the preview's notes never refetch. */
export function seedPreviewNotes(qc: QueryClient) {
  qc.setQueryDefaults(notesKeys.all, {
    staleTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  for (const n of [...PICKER_NOTES, PREVIEW_WRITING_NOTE]) {
    qc.setQueryData(notesKeys.detail(n.id), n);
  }
  const list = { items: PICKER_NOTES, page: 1, total: PICKER_NOTES.length, hasMore: false };
  qc.setQueryData(notesKeys.list({ page: 1, pageSize: 50 }), list);
  qc.setQueryData(notesKeys.list({ page: 1, pageSize: 50, search: "" }), list);
}
